// Settings → Alerts (docs/plans/shop-alerts.md, boards ShopAlertsSettings and ShopAlertsPhone):
// this phone's switch for alerts while the app is closed, and the person's own choices,
// which go to every phone they turn on. What is sent, and to whom, is the server's
// (_push_due_shop); the choices are saved online with push_prefs_set.
import { Badge, Card, Segmented, SkeletonForm, Switch, cx, useToast } from '@ims/ui'
import { Info } from '@ims/ui/icons'
import { useEffect, useState } from 'react'
import { errorMessage, useFeature, useOnline, useSettings } from '../hooks.ts'
import { PushSwitch } from '../layout/push-switch.tsx'
import { useSession } from '../session.tsx'
import { roleAtLeast, useRole, useWorkspace } from '../workspace.tsx'
import { SettingsPart } from './settings-part.tsx'

type Kind = 'low' | 'receivable' | 'payable' | 'arriving' | 'expiring'
type Hour = '7' | '8' | '9' | '10'

interface Prefs {
  approvals: boolean
  decided: boolean
  summary: boolean
  summary_hour: number
  summary_kinds: Kind[]
}

const KINDS: { kind: Kind; label: string; hint: string; managers?: true }[] = [
  { kind: 'low', label: 'Low stock', hint: 'What has run low or out, by name.' },
  {
    kind: 'receivable',
    label: 'Money from customers',
    hint: 'Due today and late, as totals. Never a customer’s name on the lock screen.',
    managers: true,
  },
  {
    kind: 'payable',
    label: 'Money to suppliers',
    hint: 'Bills due in the next 7 days.',
    managers: true,
  },
  { kind: 'arriving', label: 'Orders arriving today', hint: 'Purchase orders expected today.' },
  {
    kind: 'expiring',
    label: 'Batches expiring',
    hint: 'Batches with stock that expire soon. Only where batches are kept.',
  },
]

const HOURS: { value: Hour; label: string }[] = [
  { value: '7', label: '7:00' },
  { value: '8', label: '8:00' },
  { value: '9', label: '9:00' },
  { value: '10', label: '10:00' },
]

export function AlertSettings() {
  const { platform } = useSession()
  const { current } = useWorkspace()
  const role = useRole()
  const online = useOnline()
  const toast = useToast()
  const settings = useSettings()
  const purchasing = useFeature('purchase_control')
  const manager = roleAtLeast(role, 'manager')
  const tenantId = current?.tenant_id ?? ''
  const [prefs, setPrefs] = useState<Prefs | null>(null)

  useEffect(() => {
    if (!tenantId) return
    let active = true
    void platform.supabase
      .rpc('push_prefs_get', { p_tenant_id: tenantId })
      .then(({ data, error }) => {
        if (!active) return
        if (error) toast.error(errorMessage(error))
        else setPrefs(data as Prefs)
      })
    return () => {
      active = false
    }
  }, [platform, tenantId, toast])

  // Saved at once; the screen shows the change first and puts it back if the save fails.
  const save = async (change: Partial<Prefs>) => {
    if (!prefs) return
    const before = prefs
    setPrefs({ ...prefs, ...change })
    const { data, error } = await platform.supabase.rpc('push_prefs_set', {
      p_tenant_id: tenantId,
      p_prefs: change,
    })
    if (error) {
      setPrefs(before)
      toast.error(errorMessage(error))
    } else setPrefs(data as Prefs)
  }

  const kinds = KINDS.filter((k) => !k.managers || manager)
  const off = !online || prefs === null
  const toggleKind = (kind: Kind, on: boolean) => {
    if (!prefs) return
    const next = on
      ? [...new Set([...prefs.summary_kinds, kind])]
      : prefs.summary_kinds.filter((k) => k !== kind)
    void save({ summary_kinds: next })
  }

  return (
    <Card className="p-0">
      <SettingsPart
        title="Alerts when the app is closed"
        about="Turned on per phone. What you get is yours: it follows you to every phone you turn on."
      >
        <div className="sm:col-span-2">
          <PushSwitch label="On this phone" description="It buzzes even when it is locked." />
        </div>
      </SettingsPart>
      {prefs === null ? (
        <div className="p-4 sm:p-6">
          {online ? (
            <SkeletonForm />
          ) : (
            <p className="text-sm text-muted">Needs the internet to show your alerts.</p>
          )}
        </div>
      ) : (
        <>
          {purchasing && (
            <SettingsPart
              title="Right away"
              about="Only things that wait for someone. They come as soon as they happen."
            >
              {manager && (
                <Switch
                  className="sm:col-span-2"
                  label="Waiting for your approval"
                  description="Purchase orders and requests at a step you can decide: your role, your own limit, never one you made."
                  checked={prefs.approvals}
                  disabled={off}
                  onChange={(e) => void save({ approvals: e.target.checked })}
                />
              )}
              <Switch
                className="sm:col-span-2"
                label="Your requests decided"
                description="When a purchase order or request you sent is approved or rejected, with the reason."
                checked={prefs.decided}
                disabled={off}
                onChange={(e) => void save({ decided: e.target.checked })}
              />
            </SettingsPart>
          )}
          <SettingsPart
            title="Every morning"
            about="One alert that reads like a briefing, only on days with something to say. Tap it to open the Dashboard."
          >
            <Switch
              className="sm:col-span-2"
              label="Morning summary"
              description="Low stock, money due, orders arriving: one alert, not one for each."
              checked={prefs.summary}
              disabled={off}
              onChange={(e) => void save({ summary: e.target.checked })}
            />
            <div className={cx('sm:col-span-2', !prefs.summary && 'opacity-60')}>
              <span className="mb-1.5 block text-sm font-semibold text-ink">At (Nepal time)</span>
              <Segmented
                label="Time of the morning summary"
                value={String(prefs.summary_hour) as Hour}
                options={HOURS}
                onChange={(hour) => !off && void save({ summary_hour: Number(hour) })}
              />
            </div>
            <fieldset
              className={cx('sm:col-span-2', !prefs.summary && 'opacity-60')}
              disabled={off || !prefs.summary}
            >
              <legend className="mb-1 text-sm font-semibold text-ink">What it covers</legend>
              <ul className="divide-y divide-soft">
                {kinds.map((k) => (
                  <li key={k.kind}>
                    <label className="flex cursor-pointer items-start gap-2.5 py-2.5">
                      <input
                        type="checkbox"
                        className="mt-0.5 size-4 rounded border-border accent-primary"
                        checked={prefs.summary_kinds.includes(k.kind)}
                        onChange={(e) => toggleKind(k.kind, e.target.checked)}
                      />
                      <span className="min-w-0 flex-1 text-sm">
                        <span className="font-semibold text-ink">{k.label}</span>
                        {k.managers && (
                          <Badge tone="neutral" className="ml-1.5 align-[1px]">
                            Managers
                          </Badge>
                        )}
                        <span className="block text-xs text-muted">{k.hint}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          </SettingsPart>
          <SettingsPart
            title="How it looks"
            about={
              manager
                ? 'An example for you. Someone below manager gets the same without the money lines.'
                : 'An example for you. Money lines go to managers and above.'
            }
          >
            <SummaryPreview
              business={settings?.business_name ?? 'Your business'}
              kinds={prefs.summary ? prefs.summary_kinds : []}
              manager={manager}
              hour={prefs.summary_hour}
            />
          </SettingsPart>
        </>
      )}
      {!online && prefs !== null && (
        <p className="flex items-center gap-2 border-t border-border px-4 py-3 text-sm text-muted sm:px-6">
          <Info className="size-4 shrink-0" aria-hidden />
          Needs the internet to change these.
        </p>
      )}
    </Card>
  )
}

/** The board's notification card, with example numbers, for the parts this person chose. */
function SummaryPreview({
  business,
  kinds,
  manager,
  hour,
}: {
  business: string
  kinds: Kind[]
  manager: boolean
  hour: number
}) {
  const has = (k: Kind) => kinds.includes(k)
  const lines = [
    has('low') && '5 items ran low: Sugar 1 kg, Dettol 500 ml, Wai Wai, +2',
    manager && has('receivable') && 'Rs. 42,000 due from 3 customers today',
    manager && has('payable') && 'Rs. 65,000 to 2 suppliers due this week',
    has('arriving') && '2 orders arrive today',
    has('expiring') && '4 batches expire soon',
  ].filter((l): l is string => Boolean(l))
  const title = [
    has('low') && '5 low',
    manager && has('receivable') && 'Rs. 42,000 due',
    !has('low') && has('arriving') && '2 orders arriving',
  ]
    .filter(Boolean)
    .slice(0, 2)
    .join(', ')
  if (lines.length === 0)
    return (
      <p className="text-sm text-muted sm:col-span-2">
        No morning summary: turn it on, or pick what it covers.
      </p>
    )
  return (
    <div className="flex gap-3 rounded-card border border-border bg-surface px-3.5 py-3 shadow-card sm:col-span-2">
      <span
        aria-hidden
        className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary text-[13px] font-extrabold text-on-fill"
      >
        SG
      </span>
      <span className="min-w-0 flex-1 text-sm leading-snug">
        <span className="flex justify-between gap-2 text-xs text-subtle">
          <span className="truncate" translate="no">
            Sano Godam · {business}
          </span>
          <span>{hour}:00</span>
        </span>
        <b className="block text-ink">Good morning: {title || lines[0]}</b>
        {lines.map((l) => (
          <span key={l} className="block text-muted">
            {l}
          </span>
        ))}
      </span>
    </div>
  )
}
